import { checkoutUrlBuilder, DonationDetails } from '@classy/campaign-page-blocks'
import { logger } from '../../../utils/logger'
import type { ServiceHandler } from './service-registry'

/**
 * Redirect to checkout payload type
 */
export interface RedirectToCheckoutPayload {
  donationAmount: string
  donationFrequency: string
  currency?: string
  recurringEndDate?: string
  programDesignationId?: string
  dedication?: string
}

/**
 * REDIRECT_TO_CHECKOUT service handler
 *
 * Handles requests to redirect to the checkout page with optional extra parameters.
 *
 * Expected payload: RedirectToCheckoutPayload object
 * Expected response format:
 * - Success: { redirected: true, url: string }
 * - Error: "error message"
 */
export const redirectToCheckout: ServiceHandler = async ({
  iframeId,
  messageId,
  payload,
  sendSuccessResponse,
  sendErrorResponse,
  pageConfig,
}) => {
  const { campaignId, orgId, checkoutBaseUrl, forwardCheckoutQueryParams } = pageConfig

  try {
    const hasRequiredPageConfigData = campaignId && orgId && checkoutBaseUrl
    if (!hasRequiredPageConfigData) {
      throw new Error(
        'CustomEmbeddedCode:redirectToCheckout: Missing page config data for checkout redirect',
      )
    }

    // TODO: CL-51093 improve payload validation with Yup/Zod library
    const typedPayload = payload as unknown as RedirectToCheckoutPayload
    const {
      donationAmount,
      donationFrequency,
      currency = 'USD',
      recurringEndDate = '',
      programDesignationId,
      dedication,
    } = typedPayload

    /**
     * TODO: CL-51120: Add dedication to checkout URL
     *
     * We need to validate the dedication is enabled for the campaign from settings
     * to include dedications in the checkout url. This will be completed in a future PR.
     * If dedications are not enabled, then we can provide dedication details in the checkout url.
     * Otherwise, it will be ignored.
     */
    const showDedication = Boolean(dedication && dedication?.length > 0)

    const donationDetails: DonationDetails = {
      amount: donationAmount,
      frequency: donationFrequency,
      recurringEndDate: recurringEndDate,
      currency: currency,
      designationId: programDesignationId,
    }

    const finalUrl = checkoutUrlBuilder(
      campaignId,
      orgId,
      forwardCheckoutQueryParams || {},
      checkoutBaseUrl, // either domain masked url or classy studio domain
      donationDetails,
      // TODO: CL-51120: only include if settings has feature enabled
      dedication,
      showDedication,
    )

    window.location.href = finalUrl
    sendSuccessResponse(iframeId, messageId, { redirected: true, url: finalUrl })
  } catch (error) {
    logger(
      'error',
      new Error('CustomEmbeddedCode:redirectToCheckout: Failed to redirect to checkout', {
        cause: error,
      }),
      {
        context: { iframeId, messageId, payload, campaignId },
      },
    )
    sendErrorResponse(iframeId, messageId, 'Unable to redirect to checkout')
  }
}
